import { ref } from 'vue'
import type { Ref } from 'vue'
import type {
  AttributeValueType,
  JournalAttribute,
} from '@/views/apps/journals/types/journal.types'
import {
  formatDateForAPI,
  formatDateForInput,
  formatDateOnlyForAPI,
  formatTimeForAPI,
  formatTimeForInput,
  parseDateFromInput,
  parseDateTimeValue,
  parseTimeFromAPI,
} from '@/views/apps/journals/utils/dateUtils'

interface UseEntryDateFieldsParams {
  journalAttributes: Ref<JournalAttribute[]>
  entryForm: Ref<Record<string, AttributeValueType>>
  errors: Ref<Record<string, string>>
}

const DATE_MASK_RE = /^\d{2}\.\d{2}\.\d{4}$/
const TIME_MASK_RE = /^\d{2}:\d{2}$/

/** Парсинг/форматирование/маски для полей date, date_time и time записи журнала. */
export function useEntryDateFields({
  journalAttributes,
  entryForm,
  errors,
}: UseEntryDateFieldsParams) {
  const dateMenu = ref<Record<string, boolean>>({})
  const tempDate = ref<Record<string, Date | null>>({})
  const timeMenu = ref<Record<string, boolean>>({})
  const tempTime = ref<Record<string, Date | null>>({})
  const dateMaskedInput = ref<Record<string, string>>({})
  const timeMaskedInput = ref<Record<string, string>>({})

  const clearError = (formKey: string) => {
    if (errors.value[formKey]) {
      delete errors.value[formKey]
    }
  }

  const getDisplayDate = (formKey: string): string => {
    const value = entryForm.value[formKey]
    if (!value || typeof value !== 'string') {
      return ''
    }
    try {
      const str = value.trim()
      let date: Date | null = null
      if (DATE_MASK_RE.test(str)) {
        date = parseDateFromInput(str)
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        date = new Date(`${str}T00:00:00`)
      } else {
        date = new Date(str)
      }

      return date && !Number.isNaN(date.getTime()) ? formatDateForInput(date) : value
    } catch {
      return value
    }
  }

  const getDateForPicker = (formKey: string): Date | null => {
    const value = entryForm.value[formKey]
    if (!value || typeof value !== 'string') {
      return null
    }
    try {
      const str = value.trim()
      if (DATE_MASK_RE.test(str)) {
        return parseDateFromInput(str)
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return new Date(`${str}T00:00:00`)
      }
      const d = new Date(str)

      return Number.isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }

  const getDisplayTime = (attrName: string): string => {
    const value = entryForm.value[attrName]
    if (!value || typeof value !== 'string') {
      return ''
    }
    try {
      const date = parseTimeFromAPI(value)
      if (date && !Number.isNaN(date.getTime())) {
        return formatTimeForInput(date)
      }

      return value
    } catch {
      return value
    }
  }

  const initTimeValue = (attrName: string, value: AttributeValueType) => {
    if (value && typeof value === 'string') {
      try {
        const date = parseTimeFromAPI(value)
        tempTime.value[attrName]
          = date && !Number.isNaN(date.getTime()) ? date : null
      } catch {
        tempTime.value[attrName] = null
      }
    } else {
      tempTime.value[attrName] = null
    }
  }

  const onDateSave = (formKey: string, date: Date | null) => {
    entryForm.value[formKey] = date ? formatDateOnlyForAPI(date) : ''
    dateMaskedInput.value[formKey] = getDisplayDate(formKey)
    dateMenu.value[formKey] = false
    tempDate.value[formKey] = null
    clearError(formKey)
  }

  const onDateTimeInput = (attrName: string, isoValue: string | null) => {
    if (!isoValue) {
      entryForm.value[attrName] = ''
    } else {
      const parsed = new Date(isoValue)
      entryForm.value[attrName] = Number.isNaN(parsed.getTime())
        ? ''
        : formatDateForAPI(parsed)
    }
    clearError(attrName)
  }

  const getDateTimeIsoValue = (attrName: string): string | null => {
    const parsed = parseDateTimeValue(entryForm.value[attrName])

    return parsed ? parsed.toISOString() : null
  }

  const onTimeSave = (attrName: string, time: Date | null) => {
    entryForm.value[attrName] = time ? formatTimeForAPI(time) : ''
    timeMaskedInput.value[attrName] = getDisplayTime(attrName)
    timeMenu.value[attrName] = false
    tempTime.value[attrName] = null
    clearError(attrName)
  }

  const onDateInput = (formKey: string, value: string) => {
    dateMaskedInput.value[formKey] = value
    if (!value || !DATE_MASK_RE.test(value)) {
      entryForm.value[formKey] = ''

      return
    }

    const parsed = parseDateFromInput(value)
    entryForm.value[formKey] = parsed ? formatDateOnlyForAPI(parsed) : ''
    if (parsed) {
      clearError(formKey)
    }
  }

  const onTimeInput = (formKey: string, value: string) => {
    timeMaskedInput.value[formKey] = value
    if (!value || !TIME_MASK_RE.test(value)) {
      entryForm.value[formKey] = ''

      return
    }

    const parsed = parseTimeFromAPI(value)
    entryForm.value[formKey] = parsed ? formatTimeForAPI(parsed) : ''
    if (parsed) {
      clearError(formKey)
    }
  }

  const getDateMaskValue = (formKey: string): string =>
    dateMaskedInput.value[formKey] !== undefined
      ? dateMaskedInput.value[formKey]
      : getDisplayDate(formKey)

  const getTimeMaskValue = (formKey: string): string =>
    timeMaskedInput.value[formKey] !== undefined
      ? timeMaskedInput.value[formKey]
      : getDisplayTime(formKey)

  const syncMaskedInputs = () => {
    journalAttributes.value.forEach(attr => {
      const key = String(attr.id)
      if (attr.attribute_type === 'date') {
        dateMaskedInput.value[key] = getDisplayDate(key)
      } else if (attr.attribute_type === 'time') {
        timeMaskedInput.value[key] = getDisplayTime(key)
      }
    })
  }

  const resetDateFields = () => {
    dateMenu.value = {}
    tempDate.value = {}
    timeMenu.value = {}
    tempTime.value = {}
    dateMaskedInput.value = {}
    timeMaskedInput.value = {}
  }

  return {
    dateMaskedInput,
    dateMenu,
    getDateForPicker,
    getDateMaskValue,
    getDateTimeIsoValue,
    getDisplayDate,
    getDisplayTime,
    getTimeMaskValue,
    initTimeValue,
    onDateInput,
    onDateSave,
    onDateTimeInput,
    onTimeInput,
    onTimeSave,
    resetDateFields,
    syncMaskedInputs,
    tempDate,
    tempTime,
    timeMaskedInput,
    timeMenu,
  }
}
